import { api, setAccessToken, unwrap, type ApiEnvelope } from './api';

export interface AuthUser {
  id: string;
  email: string;
  name: string;
  createdAt: string;
}

export interface LoginInput { email: string; password: string }
export interface RegisterInput { name: string; email: string; password: string; acceptTerms: boolean }

export async function login(input: LoginInput): Promise<{ accessToken: string; user: AuthUser }> {
  // Build the payload from named fields so any client-only properties (a TS
  // type leak, a future signin "remember me" toggle, etc.) never leave the SPA.
  const payload = { email: input.email, password: input.password };
  const data = await unwrap(
    api.post<ApiEnvelope<{ accessToken: string; user: AuthUser }>>('/auth/login', payload),
  );
  setAccessToken(data.accessToken);
  return data;
}

export async function register(input: RegisterInput): Promise<{ accessToken: string; user: AuthUser }> {
  // The signup form keeps a client-only `confirm` field (password confirmation)
  // for validation. The backend uses .strict() on its Zod schema, so we must
  // strip anything not in RegisterInput before posting.
  const payload = {
    name: input.name,
    email: input.email,
    password: input.password,
    acceptTerms: input.acceptTerms,
  };
  // The register endpoint is intentionally anti-enumeration: it always returns
  // a generic { ok: true } whether or not the email was new. To still land the
  // user on the dashboard, chain a login. A pre-existing email with a different
  // password will fail this login the same way a typo would — the SPA surfaces
  // a single generic "Invalid email or password" message in either case.
  await unwrap(api.post<ApiEnvelope<{ ok: true }>>('/auth/register', payload));
  return login({ email: input.email, password: input.password });
}

export async function logout(): Promise<void> {
  try { await api.post('/auth/logout', {}); }
  finally { setAccessToken(null); }
}

export interface SessionData {
  authenticated: boolean;
  accessToken?: string;
  user?: AuthUser;
}

export async function checkSession(): Promise<{ user: AuthUser; accessToken: string } | null> {
  try {
    const res = await api.get<ApiEnvelope<SessionData>>('/auth/session');
    if (res.data.ok && res.data.data.authenticated && res.data.data.accessToken && res.data.data.user) {
      setAccessToken(res.data.data.accessToken);
      return { user: res.data.data.user, accessToken: res.data.data.accessToken };
    }
    return null;
  } catch {
    return null;
  }
}

export async function me(): Promise<AuthUser | null> {
  try {
    return await unwrap(api.get<ApiEnvelope<AuthUser>>('/auth/me'));
  } catch {
    return null;
  }
}

export async function forgotPassword(email: string): Promise<void> {
  await api.post('/auth/forgot-password', { email });
}
